import { useMemo, useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { PlansSection } from '@/components/PlansSection';
import { Grid3X3, Navigation, Shield, Code2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePaulAccess } from '@/lib/paulAccess';

const Plans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPaul, isReady } = usePaulAccess();
  const developerUnlocked = isPaul;

  const navItems = useMemo(() => {
    const base = [
      { id: 'dashboard', label: 'Dashboard', icon: <Grid3X3 className="w-4 h-4" />, to: '/admin' },
      { id: 'plans', label: 'Plans', icon: <Navigation className="w-4 h-4" />, to: '/plans' },
    ];

    if (developerUnlocked) {
      base.push({ id: 'roles', label: 'User roles', icon: <Shield className="w-4 h-4" />, to: '/roles' });
      base.push({ id: 'developer', label: 'Developer', icon: <Code2 className="w-4 h-4" />, to: '/developer' });
    }

    return base;
  }, [developerUnlocked]);

  useEffect(() => {
    if (!isReady) return;
    if (!isPaul) {
      navigate('/');
    }
  }, [isPaul, isReady, navigate]);

  return (
    <div className="min-h-screen bg-background nebula-bg star-field flex flex-col">
      {/* Sticky nav bar */}
      <nav
        className="sticky top-0 z-40 mb-4 mx-4 mt-2"
        style={{ paddingTop: 'var(--safe-area-top, 0px)' }}
        aria-label="Admin navigation"
      >
        <div className="mx-auto rounded-2xl border border-border/60 bg-background/85 backdrop-blur px-2 py-2 shadow-[0_12px_30px_rgba(0,0,0,0.25)]">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar px-1">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Button
                  key={item.id}
                  asChild
                  variant={active ? 'default' : 'ghost'}
                  size="sm"
                  className="gap-2 rounded-full px-3 py-2"
                >
                  <Link to={item.to} aria-current={active ? 'page' : undefined} className="flex items-center gap-2">
                    {item.icon}
                    <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                  </Link>
                </Button>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 pt-4 pb-[calc(3.5rem+var(--safe-area-bottom,0px))] md:pb-8 flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto space-y-8">
          <PlansSection />
        </div>
      </main>
    </div>
  );
};

export default Plans;
